import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { StyleSheet } from 'react-native';
import { DNABox, DNAButton, DNAIcon, DNAText, GridList, Iffy, luxColors } from '@alucio/lux-ui';
import {
  DNAFolderContextMenu,
  DNAFolderPinButton,
  DNAFolderStatusChip,
  useCurrentPage,
  useDNAFolderActions,
} from 'src/components/DNA';
import { FolderORM, ORMTypes } from 'src/types/types';
import { useAppSettings } from 'src/state/context/AppSettings';
import {
  COLUMN_CONFIG,
  FolderHeaderType,
  FolderVariants,
  GRID_LIST_STYLES_VARIANTS_CONFIGS,
} from 'src/screens/MyFolders/constants';
import { FOLDER_STATUS_POPOVER_TEXT } from 'src/components/DNA/Chip/DNAFolderStatusChip';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import loggers from 'src/utils/logger';

const styles = StyleSheet.create({
  sharedBadge: {
    marginLeft: 8,
  },
  badges: {
    justifyContent: 'flex-end',
  },
});

interface DNAFolderListRowProps {
  folderORM: FolderORM;
  variant: FolderVariants;
  onPress: (folderORM: FolderORM) => void;
}

const DNAFolderListRow: React.FC<DNAFolderListRowProps> = (props) => {
  const { folderORM, variant, onPress } = props;
  const { deviceMode } = useAppSettings();
  const folderActions = useDNAFolderActions();
  const route = useCurrentPage({ exact: false });
  const isTablet = deviceMode === 'tablet';
  const isSharedFolders = route?.PATH.includes('shared_folders');
  const isArchivedFolders = route?.PATH.includes('archived_folders');
  const columns = COLUMN_CONFIG[variant].columns;
  const isRootFolder = folderORM.relations.parentFolderORM === null;
  const isPinned = folderORM.meta.isFavorite;
  const displaySharedFolderBadge = !isSharedFolders && !isArchivedFolders &&
    folderORM.meta.isSharedFolder && isRootFolder && !folderORM.meta.isSharedWithTheUser;

  const iconColor =  useMemo(() => isArchivedFolders
    ? colors['color-ui2-neutral-400']
    : colors['color-ui2-brand-200'], [isArchivedFolders]);

  return (
    <GridList.Row
      interactive="hoverable"
      hoverColor={luxColors.mainContainer.primary}
      onPress={() => onPress?.(folderORM)}
      style={GRID_LIST_STYLES_VARIANTS_CONFIGS.FOLDER_ROW[deviceMode]}
    >
      {columns.map(col => {
        switch (col) {
          case FolderHeaderType.FAVORITE_BUTTON:
            return (
              <GridList.Col key={col}>
                <DNAFolderPinButton
                  onPin={() => isPinned ? folderActions.unpin(folderORM)() : folderActions.pin(folderORM)()}
                  isPinned={isPinned}
                  isHidden={!isRootFolder}
                />
              </GridList.Col>
            )
          case FolderHeaderType.FOLDER_ICON_OR_THUMBNAIL:
            return (
              <GridList.Col key={col}>
                <DNABox fill alignX="center">
                  <DNAIcon.Styled
                    name="folder-simple-fill"
                    appearance="ghost"
                    color={iconColor}
                    width={GRID_LIST_STYLES_VARIANTS_CONFIGS.FOLDER_ICON[deviceMode].width}
                    height={GRID_LIST_STYLES_VARIANTS_CONFIGS.FOLDER_ICON[deviceMode].height}
                  />
                </DNABox>
              </GridList.Col>
            )
          case FolderHeaderType.NAME:
            return (
              <GridList.Col key={col}>
                <DNAText
                  testID="folder-item-row-name"
                  numberOfLines={isTablet ? undefined : 1}
                  style={GRID_LIST_STYLES_VARIANTS_CONFIGS.TITLE_TEXT[deviceMode]}
                >
                  {folderORM.model.name}
                </DNAText>
                <Iffy is={displaySharedFolderBadge}>
                  <DNAFolderStatusChip
                    folderStatus={'SHARED'}
                    style={styles.sharedBadge}
                    popoverText={FOLDER_STATUS_POPOVER_TEXT[ORMTypes.FOLDER].SHARED}
                  />
                </Iffy>
                <Iffy is={folderORM.meta.isSharedWithTheUser && !isSharedFolders && !isArchivedFolders}>
                  <DNAFolderStatusChip
                    folderStatus={'SHARED_WITH_ME'}
                    style={styles.sharedBadge}
                    popoverText={FOLDER_STATUS_POPOVER_TEXT[ORMTypes.FOLDER].SHARED_WITH_ME}
                  />
                </Iffy>
              </GridList.Col>
            )
          case FolderHeaderType.OWNER: {
            const ownerORM = folderORM.relations.ownerORM;
            if (!ownerORM) return <GridList.Col/>;
            const ownerName = `${ownerORM?.model.givenName} ${ownerORM?.model.familyName}`;
            return (
              <GridList.Col key={col}>
                <DNAText
                  testID="folder-item-row-owner"
                  style={GRID_LIST_STYLES_VARIANTS_CONFIGS.SECONDARY_TEXT[deviceMode]}
                >
                  {ownerName}
                </DNAText>
              </GridList.Col>
            )
          }
          case FolderHeaderType.BADGES:
            return (
              <GridList.Col key={col} style={styles.badges}>
                <DNABox>
                  <DNAFolderStatusChip orm={folderORM} variant="List" />
                </DNABox>
              </GridList.Col>
            )
          case FolderHeaderType.UPDATED:
            return (
              <GridList.Col key={col}>
                <DNAText
                  testID="folder-item-row-updated"
                  style={GRID_LIST_STYLES_VARIANTS_CONFIGS.SECONDARY_TEXT[deviceMode]}
                >
                  {format(new Date(folderORM.model.updatedAt), 'MM/dd/yyyy')}
                </DNAText>
              </GridList.Col>
            )
          case FolderHeaderType.ACTION_MENU:
            return (
              <GridList.Col key={col}>
                <DNAFolderContextMenu folderORM={folderORM}>
                  <DNAButton
                    testID="folder-item-ellipsis"
                    appearance="ghost"
                    status="subtle"
                    size={isTablet ? 'xl' : 'md'}
                    iconLeft="dots-three-vertical-bold"
                  />
                </DNAFolderContextMenu>
              </GridList.Col>
            )
          default:
            loggers.util.warn('Unsupported type', col);
            return null;
        }
      })}
    </GridList.Row>
  )
}

export default DNAFolderListRow;
